/**
 * Máquina de estados da esteira (PRD, Feature 5 · spec-project-form.md §4.5).
 *
 *              ┌───────── discard_draft (soft delete)
 *              │
 *            draft ──submit──▶ under_review ──approve──▶ approved ─▶ completed
 *                                 │  └──reject──▶ rejected
 *               request_changes   │
 *                                 ▼
 *                        changes_requested ──submit──▶ (volta para under_review)
 *
 *   cancelled: sai de under_review, changes_requested e approved.
 *
 * A transição em si NUNCA acontece aqui nem por UPDATE direto — é sempre uma
 * RPC no banco (ADR-003). O que este arquivo guarda é o vocabulário: rótulos,
 * agrupamentos e tom visual.
 *
 * ⚠️ **Há UM estado de espera, e ele se chama `under_review`** (ADR-013).
 * `submitted` existiu enquanto existiu a reivindicação de análise — o passo de
 * kanban "comecei a olhar este" —, e ela saiu inteira da esteira
 * (spec-project-review-ux.md decisão 25). Os dois significavam "aguardando
 * decisão"; dois nomes para um lugar obrigavam todo `switch` exaustivo a tratar
 * um caso impossível. **O ato continua se chamando enviar**: `submit_project`,
 * `submitted_at` e a linha do tempo não mudaram de palavra. Enviar é o que a
 * pessoa fez; em análise é onde o projeto está.
 *
 * ⚠️ `in_progress` NÃO existe, e a ausência é deliberada (spec §1, decisão 3) —
 * é o precedente direto da remoção acima. Nenhuma ação humana o movia, e estado
 * que ninguém dispara é armadilha esperando alguém escrever nela. "Em execução"
 * é flag DERIVADA — `approved` e agora entre início e fim. A irmã dela,
 * `approved` e agora passado o fim, é "encerrado sem relatório", e alimenta o
 * contador de pendências. As duas moram na feature de projetos, junto de
 * `shared/lib/dates.ts`; não voltam para cá como status.
 */

export const PROJECT_STATUSES = [
  'draft',
  'under_review',
  'changes_requested',
  'approved',
  'rejected',
  'cancelled',
  'completed',
] as const;

export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  draft: 'Rascunho',
  under_review: 'Em análise',
  changes_requested: 'Ajustes solicitados',
  approved: 'Aprovado',
  rejected: 'Rejeitado',
  cancelled: 'Cancelado',
  completed: 'Concluído',
};

export type StatusTone = 'neutral' | 'info' | 'warning' | 'success' | 'danger';

export const PROJECT_STATUS_TONES: Record<ProjectStatus, StatusTone> = {
  draft: 'neutral',
  under_review: 'info',
  changes_requested: 'warning',
  approved: 'success',
  // Os dois terminais negativos compartilham o tom porque, para quem varre uma
  // lista, significam a mesma coisa: não vai acontecer. O rótulo é que
  // distingue a recusa do revisor (`rejected`) do evento que caiu
  // (`cancelled`).
  rejected: 'danger',
  cancelled: 'danger',
  completed: 'neutral',
};

/**
 * Fila de análise (US-18) — **um valor, em lista** (ADR-013).
 *
 * Eram dois, e a 2ª rodada os manteve por "tolerância a dado legado" (§3.1).
 * A migration converteu o dado; a tolerância perdeu o objeto. Continua sendo
 * lista em vez de constante solta porque quem consome a compara com `.includes`
 * e faz o `.in()` do PostgREST — e porque um segundo estado de espera, se algum
 * dia voltar a fazer sentido, entra aqui e não em cada tela.
 */
export const REVIEW_QUEUE_STATUSES = ['under_review'] as const;

/** Fase de proposta: edição livre, sem log de mudanças de campo. */
export const EDITABLE_STATUSES = ['draft', 'changes_requested'] as const;

/**
 * O que a tela de `/rascunhos` lista (decisão 49): o que está parado esperando
 * a PESSOA agir — rascunho que ela começou e projeto que voltou para ajuste.
 *
 * ⚠️ A tela se chamou "Minhas pendências" e voltou a se chamar "Rascunhos"
 * (21/08/2026). **O recorte não acompanhou o rótulo**, e é o que esta constante
 * registra: continuam sendo dois estados.
 *
 * **Lista própria, ainda que hoje ela seja idêntica a `EDITABLE_STATUSES`.** As
 * duas respondem perguntas diferentes: aquela diz o que o formulário aceita
 * receber, esta diz o que cobra ação de quem escreve. `approved` mostra que
 * elas já divergem em espírito — ele é editável (`SAVEABLE_STATUSES`, decisão
 * 18) e não é pendência de ninguém. Fundir as duas faria a lista de pendências
 * crescer no dia em que a esteira abrir um estado editável a mais.
 */
export const PENDING_STATUSES = ['draft', 'changes_requested'] as const;

/**
 * Estados que `save_project_draft` aceita. `approved` entra aqui de propósito
 * (spec §1, decisão 18): data de evento muda na vida real, e exigir nova rodada
 * de aprovação empurraria a mudança para fora do sistema. Em `approved` a
 * edição é registrada em `project_field_changes` e reexecuta o alerta de
 * conflito — mais informação, não mais burocracia.
 */
export const SAVEABLE_STATUSES = ['draft', 'changes_requested', 'approved'] as const;

/**
 * Estados considerados no alerta de conflito de espaço (US-20 / ADR-004).
 *
 * Só `approved` — mas a consulta do banco NÃO se resume a isto: ela também
 * cobre as ocorrências da agenda fixa (spec-fixed-agenda.md). Culto e EBD são
 * quem mais ocupa o prédio; comparando só projeto contra projeto, o alerta
 * quase nunca dispararia no caso que mais importa.
 */
export const CONFLICT_RELEVANT_STATUSES = ['approved'] as const;

/**
 * Estados que aparecem no calendário consolidado.
 *
 * Lista explícita, e não filtro por exclusão: acrescentar um estado ao enum no
 * futuro não deve colocá-lo no calendário por omissão. `under_review` entra
 * como "aguardando decisão", com identificação visual distinta de `approved`
 * (US-14). Ficam de fora `draft`, `changes_requested`, `rejected` e `cancelled`
 * — os quatro são "está com o ministério" ou "não vai acontecer".
 *
 * **Três, e não quatro** (regra 8, emendada pela ADR-013): o requisito de
 * produto não mudou uma vírgula — "aguardando decisão" continua se distinguindo
 * de "confirmado". O que mudou é que ele deixou de precisar de dois valores
 * para ser dito.
 */
export const CALENDAR_STATUSES = [
  'under_review',
  'approved',
  'completed',
] as const satisfies readonly ProjectStatus[];

/**
 * O que a fonte **"Meus Projetos"** do calendário carrega (spec-calendar.md
 * §3.4, decisão 79).
 *
 * Uma lista à parte porque a pergunta é outra: `CALENDAR_STATUSES` acima diz o
 * que a IGREJA vê no calendário — e ali o argumento "está com o ministério"
 * vale inteiro. Nesta lista o ministério é quem está olhando, e por isso
 * `changes_requested` entra: é o plano da pessoa para aquele dia, esperando
 * ação dela.
 *
 * ⚠️ **`draft` fica de fora, e a exclusão é ABSOLUTA** — nem com data, nem na
 * fonte da própria pessoa. Rascunho é trabalho pela metade, não plano, e o
 * critério da US-12 (*"projetos em rascunho não aparecem no calendário"*) volta
 * a valer sem exceção. Rascunho tem tela própria: `/rascunhos`.
 * `rejected` e `cancelled` também: não vão acontecer (regra 8 do CLAUDE.md).
 */
export const CALENDAR_MINE_STATUSES = [
  'under_review',
  'changes_requested',
  'approved',
  'completed',
] as const satisfies readonly ProjectStatus[];

/** Dias de espera na fila a partir dos quais o item é sinalizado (PRD §6.3). */
export const WAITING_DAYS_ALERT_THRESHOLD = 14;

export function isProjectStatus(value: string): value is ProjectStatus {
  return (PROJECT_STATUSES as readonly string[]).includes(value);
}
